import {
  ArcRotateCamera,
  // Axis,
  // Color3,
  Color4,
  CubeTexture,
  HDRCubeTexture,
  HemisphericLight,
  Matrix,
  Mesh,
  // MeshBuilder,
  // PointLight,
  Scene,
  // Space,
  // StandardMaterial,
  // SpotLight,
  Tools,
  Vector3,
} from '@babylonjs/core';

import { Fansea3DStageProps } from '../Fansea3DStage';

export const createScene = (
  canvas: any,
  engine: any,
  props: Fansea3DStageProps
) => {
  engine.setHardwareScalingLevel(0.5);

  var scene = new Scene(engine);
  scene.clearColor = new Color4(0, 0, 0, 0);
  var helper = scene.createDefaultEnvironment({
    createSkybox: true,
    enableGroundMirror: false,
    skyboxSize: 1000,
  });
  helper?.ground?.dispose();
  if (helper && helper.skyboxMaterial) {
    helper.skyboxMaterial.enableNoise = true;
  }

  // This creates and positions a free camera (non-mesh)
  if (!scene.activeCamera) {
    var camera = new ArcRotateCamera(
      'Camera',
      Math.PI,
      0.5 + Math.PI / 4,
      1,
      Vector3.Zero(),
      scene
    );

    camera.allowUpsideDown = false;
    // camera.alpha = Math.PI;
    // camera.beta = 0.5 + Math.PI / 4;
    camera.checkCollisions = false;
    camera.lowerRadiusLimit = 0.01;
    camera.maxZ = 1;
    camera.minZ = 0.03;
    camera.name = 'arc';
    camera.pinchDeltaPercentage = 0.01;
    camera.position = Vector3.Zero();
    camera.radius = 2;
    camera.target = Vector3.Zero();
    camera.upperRadiusLimit = 5;
    camera.useAutoRotationBehavior = true;
    camera.useFramingBehavior = true;
    camera.wheelDeltaPercentage = 0.01;
    camera.wheelPrecision = 50;

    camera.attachControl(canvas, true);
  }

  setEnvironmentTexture(props.backgroundTextureUrl, scene, props.onError);
  setReflectionTexture(props.reflectionTextureUrl, scene, props.onError);

  if (!scene.lights.length) {
    var hemi = new HemisphericLight('HemiLight', new Vector3(0, 30, 0), scene);
    hemi.intensity = 0.25;
  }

  // set mirror and texture
  const mirror = Mesh.CreatePlane('mirror', 10, scene);
  mirror.rotation = new Vector3(Math.PI / 2, 0, 0);
  mirror.position = new Vector3(0, 0, 0);

  // Register a render loop to repeatedly render the scene
  engine.runRenderLoop(function () {
    if (scene) scene.render();
  });

  return scene;
};

export const setEnvironmentTexture = async (
  textureUrl: string,
  scene: Scene,
  onError?: (error: Error) => void
) => {
  const backgroundIsHDRFile = textureUrl.slice(-3).toLowerCase() === 'hdr';

  const onTextureError = (message?: string, _?: any) => {
    const error = Error();
    error.message = `${message}`;
    error.name = 'TextureLoadError';
    onError?.(error);
  };

  const angle = Tools.ToRadians(0);
  const matrixRotation = Matrix.RotationY(angle);

  const textureSky = backgroundIsHDRFile
    ? new HDRCubeTexture(
        textureUrl,
        scene,
        2048,
        true, // noMipmap
        false, // generateHarmonics
        true, // gamaSpace
        undefined, // prefilterOnLoad
        null, // onLoad
        onTextureError
      )
    : new CubeTexture(
        textureUrl,
        scene,
        null,
        true,
        null,
        null,
        onTextureError
      );

  const hdrTexture = scene.environmentTexture as CubeTexture;

  textureSky.setReflectionTextureMatrix(matrixRotation);
  scene.createDefaultSkybox(textureSky, false);

  // reset reflection hdr texture or it will be lost after setting a new skybox
  hdrTexture.setReflectionTextureMatrix(Matrix.RotationY(Tools.ToRadians(0)));
  scene.environmentTexture = hdrTexture;
  scene.environmentIntensity = 0.3;
};

export const setReflectionTexture = (
  reflectionTextureUrl: string,
  scene: Scene,
  onError?: (error: Error) => void
) => {
  const reflectionIsHDRFile =
    reflectionTextureUrl.slice(-3).toLowerCase() === 'hdr';

  const onTextureError = (message?: string, _?: any) => {
    const error = Error();
    error.message = `${message}`;
    error.name = 'TextureLoadError';
    onError?.(error);
  };

  const hdrTexture = reflectionIsHDRFile
    ? new HDRCubeTexture(
        reflectionTextureUrl,
        scene,
        2048,
        true, // noMipmap
        false, // generateHarmonics
        true, // gamaSpace
        undefined, // prefilterOnLoad
        null, // onLoad
        onTextureError
      )
    : new CubeTexture(
        reflectionTextureUrl,
        scene,
        null,
        true,
        null,
        null,
        onTextureError
      );

  hdrTexture.setReflectionTextureMatrix(Matrix.RotationY(Tools.ToRadians(0)));
  scene.environmentTexture = hdrTexture;
  scene.environmentIntensity = 0.3;
};
